import { tamanhoPaginaValido } from '@/components/barra-lista/paginar-lista';
import { padroesDoTipo, type ClassificacaoProduto, type ItemFormValores, type ItemResponse, type ListarItensPaginaQuery, type SituacaoItem, type VizinhosItemQuery } from '@triade/contracts';

export type TipoItem = ItemResponse['tipo'];

export const ROTULO_TIPO: Record<TipoItem, string> = {
  PRODUTO: 'Produto fabricado',
  MERCADORIA: 'Mercadoria',
  SERVICO: 'Serviço',
  SERVICO_TERCEIRO: 'Serviço de terceiro',
  MATERIA_PRIMA: 'Matéria-prima',
  MATERIAL_SECUNDARIO: 'Material secundário',
  EMBALAGEM: 'Embalagem',
  MATERIAL_AUXILIAR: 'Material auxiliar',
};

export const ROTULO_CLASSIFICACAO: Record<ClassificacaoProduto, string> = {
  INTERMEDIARIO: 'Intermediário',
  CONJUNTO: 'Conjunto',
  ACABADO: 'Acabado',
};

/** Classificacao do produto fabricado (so PRODUTO). */
export const CLASSIFICACOES: ClassificacaoProduto[] = ['INTERMEDIARIO', 'CONJUNTO', 'ACABADO'];

export const ROTULO_SITUACAO: Record<SituacaoItem, string> = {
  ATIVO: 'Ativo',
  INATIVO: 'Inativo',
  ARQUIVADO: 'Arquivado',
};

/** Os 7 tipos (Sessao 6), na ordem das abas da lista. Ficha so PRODUTO e SERVICO; os demais sao comprados (aquisicao). */
export const TIPOS: TipoItem[] = ['PRODUTO', 'SERVICO', 'SERVICO_TERCEIRO', 'MERCADORIA', 'MATERIA_PRIMA', 'MATERIAL_SECUNDARIO', 'EMBALAGEM', 'MATERIAL_AUXILIAR'];
export const SITUACOES: SituacaoItem[] = ['ATIVO', 'INATIVO', 'ARQUIVADO'];

/** Padrao de pagina da lista (S36-D3): 50; a barra oferece 25, 50, 100 e 200. */
export const TAMANHO_PAGINA = 50;

/** Filtros da lista; tambem viajam para o detalhe, para "anterior/proximo" seguirem a lista filtrada. */
export interface BuscaItens {
  q?: string;
  tipo?: TipoItem;
  situacao?: SituacaoItem;
  /** So PRODUTO na API; a lista filtra por ela sem checar o tipo. */
  classificacao?: ClassificacaoProduto;
  /** true = so itens sem custo hoje; false = so com custo; ausente = todos (ver `ItemResponse.semCusto`). */
  semCusto?: boolean;
  /** Categoria (com as subcategorias) ou `SEM` = sem categoria (ver `ListarItensPaginaQuery.categoriaId`). */
  categoriaId?: string;
  pagina?: number;
  /** Registros por pagina (25, 50, 100 ou 200); ausente = TAMANHO_PAGINA. */
  tamanho?: number;
}

/** Busca do detalhe do item: a da lista + a aba (`?aba=recuperacao`, vinda da ficha tecnica; `?aba=tributacao`, vinda da Formacao/Liberacao/Lista sem tributos). So a rota de detalhe leva `aba`. */
export interface BuscaItemDetalhe extends BuscaItens {
  aba?: 'tributacao' | 'recuperacao';
}

// O router faz JSON.parse da URL: `?q=10874` chega como numero.
const ID = /^[A-Za-z0-9_-]{1,64}$/;
const texto = (v: unknown): string | undefined => {
  const t = typeof v === 'number' && Number.isFinite(v) ? String(v) : v;
  return typeof t === 'string' && t.trim() ? t.trim().slice(0, 100) : undefined;
};

/** Booleano da URL (`?semCusto=true`, ou o JSON.parse do router): so true/false; o resto e descartado. */
function lerSemCusto(v: unknown): boolean | undefined {
  if (v === true || v === 'true') return true;
  if (v === false || v === 'false') return false;
  return undefined;
}

/** `validateSearch` das rotas: descarta o que nao for valido em vez de quebrar a tela. */
export function lerBuscaItens(bruto: Record<string, unknown>): BuscaItens {
  const q = texto(bruto.q);
  const tipo = TIPOS.find((t) => t === bruto.tipo);
  const situacao = SITUACOES.find((s) => s === bruto.situacao);
  const classificacao = CLASSIFICACOES.find((c) => c === bruto.classificacao);
  const semCusto = lerSemCusto(bruto.semCusto);
  const categoriaId = texto(bruto.categoriaId);
  const pagina = Number(bruto.pagina);
  return {
    ...(q ? { q } : {}),
    ...(tipo ? { tipo } : {}),
    ...(situacao ? { situacao } : {}),
    ...(classificacao ? { classificacao } : {}),
    ...(semCusto !== undefined ? { semCusto } : {}),
    ...(categoriaId && ID.test(categoriaId) ? { categoriaId } : {}),
    ...(Number.isInteger(pagina) && pagina > 1 ? { pagina } : {}),
    ...(tamanhoPaginaValido(bruto.tamanho) && tamanhoPaginaValido(bruto.tamanho) !== TAMANHO_PAGINA ? { tamanho: tamanhoPaginaValido(bruto.tamanho) } : {}),
  };
}

/** `validateSearch` da rota de detalhe: a busca da lista mais `aba=recuperacao` ou `aba=tributacao` (qualquer outro valor e descartado). */
export function lerBuscaItemDetalhe(bruto: Record<string, unknown>): BuscaItemDetalhe {
  const aba = bruto.aba === 'recuperacao' ? 'recuperacao' : bruto.aba === 'tributacao' ? 'tributacao' : undefined;
  return { ...lerBuscaItens(bruto), ...(aba ? { aba } : {}) };
}

/** Filtros que valem para lista e vizinhos (tudo menos a pagina). */
export function paraFiltro(b: BuscaItens): Omit<VizinhosItemQuery, 'id'> {
  return {
    ...(b.q ? { q: b.q } : {}),
    ...(b.tipo ? { tipo: b.tipo } : {}),
    ...(b.situacao ? { situacao: b.situacao } : {}),
    ...(b.classificacao ? { classificacao: b.classificacao } : {}),
    ...(b.semCusto !== undefined ? { semCusto: b.semCusto } : {}),
    ...(b.categoriaId ? { categoriaId: b.categoriaId } : {}),
  };
}

export function paraConsulta(b: BuscaItens): Partial<ListarItensPaginaQuery> {
  const pagina = b.pagina ?? 1;
  const tamanho = b.tamanho ?? TAMANHO_PAGINA;
  return { limit: tamanho, offset: (pagina - 1) * tamanho, ...paraFiltro(b) };
}

export function temFiltro(b: BuscaItens): boolean {
  return Boolean(b.q || b.tipo || b.situacao || b.classificacao || b.semCusto !== undefined || b.categoriaId);
}

/** Situacao do cadastro a partir do que a API devolve. */
export function situacaoDe(i: Pick<ItemResponse, 'ativo' | 'arquivadoEm'>): SituacaoItem {
  return i.arquivadoEm ? 'ARQUIVADO' : i.ativo ? 'ATIVO' : 'INATIVO';
}

// As chaves de unidade de custo existem (mesmo undefined): sem elas o formulario novo ficaria sujo ao registrar os campos.
export const FORM_VAZIO: ItemFormValores = { tipo: 'PRODUTO', codigo: '', descricao: '', unidade: 'UN', zfm: false, ativo: true, classificacao: undefined, unidadeCusto: undefined, fatorCustoVenda: undefined, categoriaId: undefined, ...padroesDoTipo('PRODUTO') };

export function deResposta(i: ItemResponse): ItemFormValores {
  const padrao = padroesDoTipo(i.tipo);
  return {
    tipo: i.tipo,
    codigo: i.codigo,
    descricao: i.descricao,
    unidade: i.unidade,
    zfm: i.zfm,
    ativo: i.ativo,
    compoeFichaTecnica: i.compoeFichaTecnica ?? padrao.compoeFichaTecnica,
    comerciavel: i.comerciavel ?? padrao.comerciavel,
    // A chave existe sempre (mesmo undefined): o formulario compara por chaves e nao pode ficar sujo ao desfazer.
    classificacao: i.classificacao ?? undefined,
    // Sessao 17: a unidade de custo e o fator para venda. Sem unidade de custo, o fator (1 por padrao da API) nao aparece.
    unidadeCusto: i.unidadeCusto ?? undefined,
    fatorCustoVenda: i.unidadeCusto ? i.fatorCustoVenda : undefined,
    // Categoria: sem categoria = undefined (a chave existe, para o formulario nao sujar ao carregar).
    categoriaId: i.categoriaId ?? undefined,
  };
}
